import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import DetectRTC from 'detectrtc';
import { useShallow } from 'zustand/react/shallow';

import {
  useMediaSettingsStore,
  usePeersStore,
  useRoomActionsStore,
  useRoomStore,
  useVideoStreamStore,
} from '@src/stores';

import { useBackgroundEffectsModal, useEnumerateDevices, useFetchProfileInfo, useMediaSettings } from '@src/hooks';
import { useVirtualBackgroundPreview } from './virtualBackground/useVirtualBackgroundPreview';
import { useMeetQuery } from '@src/hooks/queries/useMeetQuery';

import { LocalStorage, virtualBackgroundService } from '@src/services';

import { getRoomId, isHtml } from '@src/helpers';
import { getCookie, setCookie } from '@src/utils';

import { sendEventToWebView } from '@src/bridge/bridgeMessenger';
import { EWebViewEvents } from '@src/bridge/types';

const useMediaPreview = ({ deniedPermissions = false } = {}) => {
  const [inputValue, setInputValue] = useState<string>('');
  const [snapshot, setSnapshot] = useState<string | null>(null);
  const [isRotating, setIsRotating] = useState(false);
  const [isLandscape, setIsLandscape] = useState(window.innerWidth > window.innerHeight);
  const [isJoinLoading, setIsJoinLoading] = useState(false);
  const MAX_NICKNAME = 32;

  const [limitReached, setLimitReached] = useState(false);

  // Internal ref for iOS snapshot capture - kept for compatibility
  const videoElementRef = useRef<HTMLVideoElement | null>(null);
  const hasRequestedDevicesRef = useRef(false);
  const isIosDevice = DetectRTC.osName === 'iOS';

  // Fetch room data for room settings (this will set the SIMD headers)
  const { data: meetRoomData } = useMeetQuery(getRoomId());

  const {
    handleCloseModal,
    handleApplyChanges,
    isApplyButtonDisabled,
    removeBackgroundEffect,
    handleOpenEffectsModal,
    isBackgroundEffectsOpen,
    selectedBackgroundEffect,
    onSelectBackgroundEffect,
    isPreviewEffectEnabled,
  } = useBackgroundEffectsModal();

  const {
    micSettings: { isMicClosed },
    camSettings: { isCamClosed },
  } = useRoomActionsStore(
    useShallow((state) => ({
      micSettings: state.micSettings,
      camSettings: state.camSettings,
    }))
  );

  const { peerName, setPeerName, setIsTriggerJoinRoom, joinFormErrors, setJoinFormErrors } = useRoomStore(
    useShallow((state) => ({
      peerName: state.peerName,
      setPeerName: state.setPeerName,
      setIsTriggerJoinRoom: state.setIsTriggerJoinRoom,
      joinFormErrors: state.joinFormErrors,
      setJoinFormErrors: state.setJoinFormErrors,
    }))
  );

  const { setAvatarUrl } = usePeersStore(
    useShallow((state) => ({
      setAvatarUrl: state.setAvatarUrl,
    }))
  );

  const { isAudioAllowed, isVideoAllowed, speakerDevices, videoInputDevices, audioInputDevices } =
    useMediaSettingsStore(
      useShallow((state) => ({
        isAudioAllowed: state.mediaSettings.isAudioAllowed,
        isVideoAllowed: state.mediaSettings.isVideoAllowed,
        speakerDevices: state.mediaSettings.speakerDevices,
        audioInputDevices: state.mediaSettings.audioInputDevices,
        videoInputDevices: state.mediaSettings.videoInputDevices,
      }))
    );

  const { originalStream, modifiedStream } = useVideoStreamStore(
    useShallow((state) => ({
      originalStream: state.originalStream,
      modifiedStream: state.modifiedStream,
    }))
  );

  const { initEnumerateDevices } = useEnumerateDevices();
  const { data: profileInfo, isPending: isProfilePending } = useFetchProfileInfo();

  const {
    selectedCamera,
    selectedAudio,
    selectedSpeaker,
    isMicDisabled,
    isCamDisabled,
    handleAudio,
    handleVideo,
    handleChangeMediaDevice,
  } = useMediaSettings();

  const localStorageInstance = useMemo(() => new LocalStorage(), []);

  const isSubmitButtonDisabled = useMemo(
    () => !(peerName.trim().length >= 2) || !!joinFormErrors.username,
    [joinFormErrors, peerName]
  );

  const hasMinLength = (name: string) => {
    return name.trim().length >= 2;
  };

  const handleChangeUsername = useCallback(
    (text: string) => {
      const sliced = text.slice(0, MAX_NICKNAME);
      setInputValue(sliced);
      setIsJoinLoading(false);
      const cleanedText = text.trim().split(' ').join(' ');
      const newName = filterXSS(cleanedText);
      const roomId = getRoomId();
      setLimitReached(newName.length === MAX_NICKNAME);

      if (isHtml(newName)) {
        setJoinFormErrors({ username: 'Invalid name!' });
      } else if (!hasMinLength(newName)) {
        setJoinFormErrors({ username: 'Nickname must contain at least 2 letters' });
      } else {
        setJoinFormErrors({ username: '' });
      }

      if (!getCookie(roomId + '_name')) {
        window.localStorage.peer_name = newName;
      }
      setCookie(roomId + '_name', newName, 30);
      localStorageInstance.setItemLocalStorage('peer_name', newName);
      setPeerName(newName);
    },
    [localStorageInstance, setJoinFormErrors, setPeerName]
  );

  // PREVIEW INTEGRATION: Get setVideoRef callback for stream management
  // The hook handles all stream selection (preview/modified/original) internally
  const { setVideoRef: setPreviewVideoRef, cleanup: cleanupPreview } = useVirtualBackgroundPreview({
    enabled: isPreviewEffectEnabled,
  });

  // Combined callback ref that:
  // 1. Passes element to preview hook for stream management
  // 2. Keeps internal ref for iOS snapshot functionality
  const setVideoRef = useCallback(
    (videoEl: HTMLVideoElement | null) => {
      videoElementRef.current = videoEl;
      setPreviewVideoRef(videoEl);
    },
    [setPreviewVideoRef]
  );

  useEffect(() => {
    if (joinFormErrors?.username) {
      setIsJoinLoading(false);
    }
  }, [joinFormErrors]);

  const handleJoinMeeting = useCallback(async () => {
    if (peerName.trim().length) {
      setIsJoinLoading(true);

      sendEventToWebView({ eventType: EWebViewEvents.MINIMIZABLE_TOOLBAR });

      setTimeout(() => {
        setIsTriggerJoinRoom(true);
      }, 400);
    }
  }, [peerName, setIsTriggerJoinRoom]);

  const captureSnapshot = useCallback((videoElement: HTMLVideoElement): string => {
    const rawOrientation: number = (window.orientation as number) || 0;
    const absOrientation = Math.abs(rawOrientation);
    const canvas: HTMLCanvasElement = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Canvas 2D context not available.');
    }

    const width = videoElement.videoWidth;
    const height = videoElement.videoHeight;

    if (absOrientation < 5 || absOrientation > 175) {
      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(videoElement, 0, 0, width, height);
    } else if (absOrientation === 90) {
      canvas.width = height;
      canvas.height = width;
      ctx.save();
      if (rawOrientation === 90) {
        ctx.translate(canvas.width, 0);
        ctx.rotate(Math.PI / 2);
      } else if (rawOrientation === -90) {
        ctx.translate(0, canvas.height);
        ctx.rotate(-Math.PI / 2);
      }
      ctx.drawImage(videoElement, 0, 0, width, height);
      ctx.restore();
    } else {
      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(videoElement, 0, 0, width, height);
    }

    return canvas.toDataURL('image/png');
  }, []);

  useEffect(() => {
    if (deniedPermissions) return;

    const fetchMediaDevices = () => {
      if (!hasRequestedDevicesRef.current) {
        hasRequestedDevicesRef.current = true;
        initEnumerateDevices();
      }
    };

    fetchMediaDevices();
  }, [initEnumerateDevices, deniedPermissions]);

  useEffect(() => {
    let resizeDebounceTimeout: NodeJS.Timeout | null = null;

    const handleResize = () => {
      setIsLandscape(window.innerWidth > window.innerHeight);
      // Trigger overlay immediately on a resize event
      setIsRotating(true);
      resizeDebounceTimeout && clearTimeout(resizeDebounceTimeout);
      resizeDebounceTimeout = setTimeout(() => {
        setIsRotating(false);
      }, 1000);
    };

    window.addEventListener('resize', handleResize);

    // Add cleanup for page refresh/unload to prevent memory leaks
    const handleBeforeUnload = () => {
      const { originalStream, modifiedStream } = useVideoStreamStore.getState();
      if (originalStream) {
        originalStream.getTracks().forEach((track) => track.stop());
      }
      if (modifiedStream) {
        modifiedStream.getTracks().forEach((track) => track.stop());
      }

      // Clean up local preview resources (canvas streams, worker refs)
      cleanupPreview();

      // Also release the virtual background worker
      virtualBackgroundService.releaseWorker();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      resizeDebounceTimeout && clearTimeout(resizeDebounceTimeout);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [cleanupPreview]);

  useEffect(() => {
    if (isProfilePending) return;
    const userName = profileInfo?.data?.name || localStorageInstance.getItemLocalStorage('peer_name') || '';
    const name = userName.slice(0, MAX_NICKNAME);
    if (profileInfo?.data?.imageFullPath) {
      setAvatarUrl(profileInfo.data.imageFullPath);
    }

    setPeerName(name);
    setInputValue(name);
    setLimitReached(name.trim().length === MAX_NICKNAME);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileInfo, isProfilePending, setAvatarUrl]);

  useEffect(() => {
    if (isRotating && videoElementRef.current && isIosDevice) {
      setSnapshot(captureSnapshot(videoElementRef.current));
    } else {
      setSnapshot(null);
    }
  }, [captureSnapshot, isIosDevice, isRotating]);

  return {
    errors: joinFormErrors,
    peerName,
    inputValue,
    originalStream: modifiedStream || originalStream, // Use processed stream if VB is active
    isMicDisabled,
    isCamDisabled,
    isAudioAllowed,
    isVideoAllowed,
    speakerDevices,
    selectedAudio,
    selectedCamera,
    selectedSpeaker,
    videoInputDevices,
    audioInputDevices,
    isSubmitButtonDisabled,
    isIosDevice,
    isLandscape,
    isRotating,
    snapshot,

    handleAudio,
    handleVideo,
    handleJoinMeeting,
    handleChangeUsername,
    handleChangeMediaDevice,

    // virtual background states
    setVideoRef,
    isBackgroundEffectsOpen,
    isApplyButtonDisabled,
    selectedBackgroundEffect,
    onSelectBackgroundEffect,
    handleOpenEffectsModal,
    removeBackgroundEffect,
    handleApplyChanges,
    handleCloseModal,

    isMicClosed,
    isCamClosed,
    MAX_NICKNAME,
    limitReached,
    isJoinLoading,
  };
};

export default useMediaPreview;
